import type { CreateProjectInput, Project } from '@/entities/project/model'
import { demoDb } from '@/shared/lib/demoDb'
import { isDemoMode, supabase } from '@/shared/lib/supabase'
import { applyScope, getDataScope, withScope } from '@/shared/lib/dataScope'
import { createTimelineEvent } from '@/features/projects/lib/timeline'
import { projectDocumentsApi } from '@/features/projects/api/projectDocuments.api'
import { recomputeCompleteness } from '@/services/project/autoLinkService'

/** Generate next sequential project number: PRJ-001, PRJ-002, etc. */
async function nextProjectNumber(scope: any): Promise<string> {
  if (!supabase) return `PRJ-${String(Date.now()).slice(-3)}`
  const query = applyScope(
    supabase.from('projects').select('number').is('deleted_at', null).order('created_at', { ascending: false }).limit(200),
    scope,
  )
  const { data } = await query
  let maxSeq = 0
  for (const row of data ?? []) {
    const num = row.number as string
    // Match PRJ-NNN (new format)
    const newMatch = num?.match(/^PRJ-(\d+)$/)
    if (newMatch) { maxSeq = Math.max(maxSeq, parseInt(newMatch[1], 10)); continue }
    // Match PRJ/YYYY/NNNN (old format) — count them too
    const oldMatch = num?.match(/^PRJ\/\d{4}\/\d+$/)
    if (oldMatch) { maxSeq = Math.max(maxSeq, maxSeq + 0) }
  }
  // Also count total projects as fallback to never go below existing count
  const total = (data ?? []).length
  const next = Math.max(maxSeq + 1, total + 1 > maxSeq + 1 ? total + 1 : maxSeq + 1)
  return `PRJ-${String(next).padStart(3, '0')}`
}

export const projectsApi = {
  async list(companyId: string): Promise<Project[]> {
    if (isDemoMode || !supabase) return Promise.resolve(demoDb.projects.list(companyId))
    const scope = await getDataScope(companyId)
    const cols = 'id, company_id, client_id, number, name, status, start_date, end_date, address, investment_address, notes, completeness_score, completeness_flags, archived_at, created_at'
    const query = applyScope(supabase.from('projects').select(cols).is('deleted_at', null).order('created_at', { ascending: false }).limit(50), scope)
    const { data, error } = await query
    if (error) throw error
    return (data ?? []).map((row: any) => ({ id: row.id, company_id: row.company_id ?? companyId, client_id: row.client_id, number: row.number, name: row.name, status: row.status, start_date: row.start_date, end_date: row.end_date, address: row.address ?? '', investment_address: row.investment_address ?? null, notes: row.notes ?? '', completeness_score: Number(row.completeness_score ?? 0), completeness_flags: row.completeness_flags ?? null, archived_at: row.archived_at ?? null, created_at: row.created_at }))
  },
  async create(input: CreateProjectInput): Promise<Project> {
    if (isDemoMode || !supabase) return Promise.resolve(demoDb.projects.create(input))
    const scope = await getDataScope(input.company_id)
    const number = await nextProjectNumber(scope)
    const payload = withScope(scope, { number, client_id: input.client_id, name: input.name, status: input.status, start_date: input.start_date, end_date: input.end_date, address: input.address ?? null, investment_address: input.investment_address ?? null, notes: input.notes ?? null, completeness_score: 0, completeness_flags: {} })
    const { data, error } = await supabase.from('projects').insert(payload).select('*').single()
    if (error) throw error
    const project: Project = { id: data.id, company_id: data.company_id ?? input.company_id, client_id: data.client_id, number: data.number, name: data.name, status: data.status, start_date: data.start_date, end_date: data.end_date, address: data.address ?? '', investment_address: data.investment_address ?? null, notes: data.notes ?? '', completeness_score: Number(data.completeness_score ?? 0), completeness_flags: data.completeness_flags ?? null, archived_at: data.archived_at ?? null, created_at: data.created_at }
    createTimelineEvent({ company_id: project.company_id, project_id: project.id, event_type: 'project_created', visibility: 'internal', title: `Projekt ${project.number} został utworzony`, actor_type: 'operator', payload: { project_name: project.name, project_number: project.number } }).catch((err) => console.warn('[projects] timeline event failed:', err))
    return project
  },
  async update(id: string, input: Partial<Project>, companyId?: string) {
    if (isDemoMode || !supabase) return Promise.resolve(demoDb.projects.update(id, input))
    const scope = await getDataScope(companyId)
    const query = applyScope(supabase.from('projects').update(input).eq('id', id).select('*').single(), scope)
    const { data, error } = await query
    if (error) throw error
    return data
  },
  async createFromEstimate(companyId: string, estimateId: string) {
    if (isDemoMode || !supabase) {
      const project = demoDb.projects.createFromEstimate(companyId, estimateId)
      // Powiąż wycenę z projektem (project_id na estimate)
      const est = demoDb.estimates.list(companyId).find((e: any) => e.id === estimateId)
      if (est) demoDb.estimates.update(estimateId, { ...est, project_id: project.id })
      // Kaskadowo powiąż powiązane umowy
      const relatedContracts = (demoDb.contracts.list(companyId) as any[]).filter((c: any) => c.estimate_id === estimateId)
      for (const contract of relatedContracts) {
        demoDb.contracts.update(contract.id, { project_id: project.id })
        // Faktury z tej umowy
        const relatedInvoices = (demoDb.invoices.list(companyId) as any[]).filter((inv: any) => inv.contract_id === contract.id)
        for (const invoice of relatedInvoices) {
          demoDb.invoices.update(invoice.id, { project_id: project.id })
        }
      }
      return Promise.resolve(project)
    }
    const scope = await getDataScope(companyId)
    // Pobierz dane wyceny
    const { data: est, error: estErr } = await supabase
      .from('cost_estimates')
      .select('id, client_id, name, project_id')
      .eq('id', estimateId)
      .single()
    if (estErr || !est) throw new Error('Nie znaleziono wyceny')
    // Jeśli wycena ma już projekt — zwróć go
    if (est.project_id) {
      const { data: existingProj } = await applyScope(
        supabase.from('projects').select('*').eq('id', est.project_id).single(),
        scope,
      )
      if (existingProj) return existingProj
    }
    // Utwórz nowy projekt
    const number = await nextProjectNumber(scope)
    const payload = withScope(scope, {
      number,
      client_id: est.client_id,
      name: est.name || 'Projekt z kosztorysu',
      status: 'offer',
      start_date: null,
      end_date: null,
      address: null,
      investment_address: null,
      notes: null,
      completeness_score: 0,
      completeness_flags: {},
    })
    const { data, error } = await supabase.from('projects').insert(payload).select('*').single()
    if (error) throw error
    // Powiąż wycenę z projektem (FK + project_documents + completeness)
    await supabase.from('cost_estimates').update({ project_id: data.id }).eq('id', estimateId)
    try {
      await projectDocumentsApi.link(companyId, data.id, 'estimate', estimateId, { manual: true })
    } catch (err) {
      console.warn('[createFromEstimate] project_documents link failed:', err)
    }
    // Kaskadowo powiąż umowy powiązane z tą wyceną
    const { data: relatedContracts } = await supabase
      .from('contracts')
      .select('id')
      .eq('estimate_id', estimateId)
    if (relatedContracts?.length) {
      const contractIds = relatedContracts.map((c: any) => c.id)
      await supabase.from('contracts').update({ project_id: data.id }).in('id', contractIds)
      // Kaskadowo powiąż faktury z tych umów
      const { data: relatedInvoices } = await supabase
        .from('invoices')
        .select('id')
        .in('contract_id', contractIds)
      if (relatedInvoices?.length) {
        await supabase.from('invoices').update({ project_id: data.id }).in('id', relatedInvoices.map((i: any) => i.id))
      }
    }
    // Przelicz completeness_flags (has_estimate, has_contract, itp.)
    recomputeCompleteness(data.id, companyId).catch((err) =>
      console.warn('[createFromEstimate] recomputeCompleteness failed:', err),
    )
    return { id: data.id, company_id: data.company_id ?? companyId, client_id: data.client_id, number: data.number, name: data.name, status: data.status, start_date: data.start_date, end_date: data.end_date, address: data.address ?? '', investment_address: data.investment_address ?? null, notes: data.notes ?? '', completeness_score: data.completeness_score ?? 0, completeness_flags: data.completeness_flags ?? {}, created_at: data.created_at }
  },
  async updateStatus(id: string, status: Project['status'], companyId?: string) { if (isDemoMode || !supabase) { demoDb.projects.updateStatus(id, status); return Promise.resolve() } const scope = await getDataScope(companyId); const query = applyScope(supabase.from('projects').update({ status }).eq('id', id), scope); const { error } = await query; if (error) throw error; if (companyId) createTimelineEvent({ company_id: companyId, project_id: id, event_type: 'project_status_changed', visibility: 'internal', title: 'Zmiana statusu projektu', actor_type: 'operator', payload: { new_status: status } }).catch((err) => console.warn('[projects] timeline event failed:', err)) },
  async delete(id: string, companyId?: string) { if (isDemoMode || !supabase) { demoDb.projects.delete(id); return Promise.resolve() } const scope = await getDataScope(companyId); const query = applyScope(supabase.from('projects').update({ deleted_at: new Date().toISOString() }).eq('id', id), scope); const { error } = await query; if (error) throw error },
  async hardDelete(id: string, _companyId?: string) {
    // Note: companyId arg kept for backward compatibility with existing callers
    // but ignored — server derives company from project_id and authorizes via auth.uid()
    // (see migration 140_security_hardening_p0).
    if (isDemoMode || !supabase) { demoDb.projects.delete(id); return Promise.resolve() }
    const { error } = await supabase.rpc('delete_project_hard', {
      p_project_id: id,
    })
    if (error) throw error
  },
}
